import { cx } from "@suse-o11y/class-variance";
import React from "react";

import { HiddenTooltipProvider, useIsTooltipHidden, useTooltipCollisionBoundaries } from "./context";

const TooltipPortal = React.lazy(async () => {
	const Tooltip = await import("@radix-ui/react-tooltip");

	return { default: Tooltip.Portal };
});

const TooltipContent = React.lazy(async () => {
	const Tooltip = await import("@radix-ui/react-tooltip");

	return { default: Tooltip.Content };
});

const TooltipArrow = React.lazy(async () => {
	const Tooltip = await import("@radix-ui/react-tooltip");

	return { default: Tooltip.Arrow };
});

export type TooltipProps = Omit<React.ComponentPropsWithoutRef<typeof TooltipContent>, "asChild">;

export const Tooltip = React.forwardRef<React.ComponentRef<typeof TooltipContent>, TooltipProps>(
	({ className, style, onClick, children, ...props }, ref) => {
		const hidden = useIsTooltipHidden();
		const collisionBoundaries = useTooltipCollisionBoundaries();

		if (hidden || !children) {
			return null;
		}

		return (
			<React.Suspense>
				{/* Disable nested tooltips */}
				<HiddenTooltipProvider value>
					<TooltipPortal>
						<TooltipContent
							arrowPadding={4} // avoid rendering the arrow on the rounded corners
							collisionBoundary={collisionBoundaries}
							collisionPadding={8} // avoid sticking to the edges of the boundaries
							sideOffset={4} // distance from the trigger
							{...props}
							className={cx("suse-o11y/tooltip", className)}
							onClick={(event) => {
								// do not propagate the click to the trigger
								// React forwards the event even when the elements are mounted in different parts of the DOM tree
								event.stopPropagation();

								onClick?.(event);
							}}
							ref={ref}
							style={
								{
									"--content-transform-origin": "var(--radix-tooltip-content-transform-origin)",
									"--content-available-width": "var(--radix-tooltip-content-available-width)",
									"--content-available-height": "var(--radix-tooltip-content-available-height)",
									"--trigger-width": "var(--radix-tooltip-trigger-width)",
									"--trigger-height": "var(--radix-tooltip-trigger-height)",
									...style,
								} as React.CSSProperties
							}
						>
							{children}
							<TooltipArrow className={cx("suse-o11y/tooltip-arrow")} height={6} width={12} />
						</TooltipContent>
					</TooltipPortal>
				</HiddenTooltipProvider>
			</React.Suspense>
		);
	},
);

Tooltip.displayName = "Tooltip";
